/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import { ChevronRight } from 'lucide-react';
import { redirect, useRouteLoaderData } from 'react-router';
import type { loader as rootLoader } from '@/root';
import { isDesignModeActive, isPreviewModeActive } from '@salesforce/storefront-next-runtime/design/mode';
import type { Route } from './+types/_app.learn.$';
import { Region } from '@/components/region';
import { Link } from '@/components/link';
import { getConfig } from '@salesforce/storefront-next-runtime/config';
import { fetchPageWithComponentData, type PageWithComponentData } from '@/lib/page-designer/page-loader.server';
import { getArticleFolder } from '@/lib/learn/taxonomy.server';
import {
    getLearnCatalog,
    getLearnListing,
    resolveArticleIdIgnoringCase,
    ARTICLE_LISTING_PAGE_TYPE,
    type LearnListing,
} from '@/lib/learn/catalog.server';
import LearnListingView from '@/components/learn-listing';
import { BreadcrumbSection } from '@/components/breadcrumb-section';
import { SeoMeta } from '@/components/seo-meta';
import { JsonLd } from '@/components/json-ld';
import { buildCanonicalUrl } from '@/utils/canonical-url';
import { getPublicOrigin } from '@/utils/schema-url';
import { resolvePageMetaDescription } from '@/lib/seo/description';
import { resolvePageTitle } from '@/lib/seo/title';
import { resolveOgImage } from '@/lib/seo/og-image';
import { LISTING_REGION_IDS } from '@/lib/learn/listing-regions';
import { generateArticleSchema, type ArticleSchema } from '@/utils/article-schema';
import { buildPageName, type RouteAnalytics } from '@/analytics/data-layer';
import { widenImageUrl } from '@/lib/images/widen-image';
import { getLogger } from '@/lib/logger.server';
import { PageType } from '@/lib/decorators/page-type';
import { RegionDefinition } from '@/lib/decorators';
import { AttributeDefinition } from '@/lib/decorators/attribute-definition';
import { type WidenImageValue } from '@/components/widen-image';
import { type DefaultSettingsValue, type DefaultSettingsTheme } from '@/lib/page-designer/default-settings';
import { resolveLastComponentTheme } from '@/lib/page-designer/last-component-theme';

/* v8 ignore start - decorators are exercised by the generate-cartridge step, not unit tests */
/**
 * Article page type (UCR-2725). Merchandisers create one published page per article (the page ID is
 * the URL slug) and author the body in the `main` region. The Thumbnail and Description page settings
 * feed Learn listings (and SEO). Routed under `/learn` (this file) — see the loader below.
 */
@PageType({
    name: 'Article',
    description:
        'A standalone article rendered under /learn. Author the body in the Article Content region; the Thumbnail and Description page settings feed Learn listings and SEO.',
    supportedAspectTypes: [],
})
@RegionDefinition([
    {
        id: 'main',
        name: 'Article Content',
        description: 'The article body — accepts any Page Designer component.',
    },
])
export class ArticlePageMetadata {
    @AttributeDefinition({
        id: 'thumbnail',
        name: 'Thumbnail',
        description: 'Widen DAM image used as the article thumbnail in Learn listings.',
        type: 'custom',
        editorDefinition: {
            type: 'inline.widenImage',
            configuration: {
                showSizes: false,
                showImageAlignment: false,
                showQualitySlider: false,
                showGridCoordinate: false,
            },
        },
    })
    thumbnail?: WidenImageValue;

    @AttributeDefinition({
        id: 'articleDescription',
        name: 'Description',
        description:
            'Short article summary shown on Learn listing cards. For the search/social meta description, fill in SEO Description under the page settings instead.',
        type: 'text',
    })
    articleDescription?: string;

    @AttributeDefinition({
        id: 'articleEyebrow',
        name: 'Eyebrow',
        description:
            'Short label shown above the title in the Featured Articles stacked layouts (e.g. "5 minute read"). Optional.',
        type: 'string',
    })
    articleEyebrow?: string;
}

/**
 * Article Listing page type (UCR-2756). Lets merchandisers add Page Designer content to a `/learn`
 * article listing. There is no platform aspect — SCAPI `getPages` keys aspect assignments on
 * category/product only, and Learn listings are content-library *folders*. Instead the merchandiser
 * **files an Article Listing page in the content folder** it applies to (the same folders articles
 * live in); the listing route resolves it by that folder via the `pd-taxonomy` catalog and renders
 * these regions around the auto-generated article grid. Folders without a page render the default
 * listing unchanged.
 */
@PageType({
    name: 'Article Listing',
    description:
        "Page Designer content for a /learn article listing. File this page in the content-library folder it applies to; its regions render around that folder's article grid.",
    supportedAspectTypes: [],
})
@RegionDefinition([
    {
        id: 'learnTopFullWidth',
        name: 'Top Full Width Region',
        description: 'Full-window-width region at the top of the listing, flush to the header.',
        maxComponents: 5,
    },
    {
        id: 'learnTopContent',
        name: 'Top Content Region',
        description: 'Content-width region directly above the article grid.',
        maxComponents: 5,
    },
    {
        id: 'learnBottom',
        name: 'Bottom Region',
        description: 'Region below the article grid.',
        maxComponents: 5,
    },
])
export class ArticleListingPageMetadata {
    @AttributeDefinition({
        id: 'settings',
        name: 'Default Settings',
        description:
            'Theme applied to the article-grid section. Defaults to light; set to dark for a black background behind the grid (like the Product Listing Page).',
        type: 'custom',
        editorDefinition: { type: 'inline.defaultSettings' },
    })
    settings?: DefaultSettingsValue;

    @AttributeDefinition({
        id: 'title',
        name: 'Title',
        description:
            'Optional heading shown above the article grid. Replaces the default folder / "Learn" title. Does not change the page ID or its URL.',
        type: 'string',
        required: false,
    })
    title?: string;
}
/* v8 ignore stop */

/** A breadcrumb crumb: a display label and the `/learn` path it links to. */
export type LearnCrumb = { name: string; href: string };

/**
 * Discriminated union the loader returns. The splat route serves **either** an Article page (the
 * last segment is an Article page ID) **or** a Learn listing (the full path is a library folder).
 */
export type LearnRouteData =
    | {
          kind: 'article';
          page: PageWithComponentData;
          footerTheme?: DefaultSettingsTheme;
          pageUrl: string;
          title: string;
          /** Authored meta description from the page's SEO settings. Undefined when unset. (UCR-3023) */
          metaDescription?: string;
          /** Social share image: Thumbnail setting → first image in the body → Black Magic mark. */
          ogImageUrl: string;
          breadcrumb: LearnCrumb[];
          /** Article JSON-LD (schema.org @graph). Null when there's no title to describe. (UCR-2783) */
          articleSchema: ArticleSchema | null;
          /** Adobe Analytics data-layer payload (UCR-2938). */
          analytics: RouteAnalytics;
      }
    | {
          kind: 'listing';
          listing: LearnListing;
          page: PageWithComponentData | null;
          footerTheme?: DefaultSettingsTheme;
          pageUrl: string;
          /** Authored SEO Title from the Article Listing page, else the folder's name. (UCR-3060) */
          title: string;
          /** Authored meta description from the Article Listing page, when a merchandiser filed one. */
          metaDescription?: string;
          /** Social share image: first image in the listing's authored regions → Black Magic mark. */
          ogImageUrl: string;
          /** Adobe Analytics data-layer payload (UCR-2938). */
          analytics: RouteAnalytics;
      };

/** Analytics payload for a Learn listing / content hub (UCR-2938): `content` page, name from the folder path. */
function learnListingAnalytics(listing: LearnListing): RouteAnalytics {
    const segments = listing.folder?.path ? listing.folder.path.split('/').filter(Boolean) : [];
    return { page: { pageName: buildPageName('learn', ...segments), pageType: 'content' } };
}

/**
 * Tab title for a Learn listing: the Article Listing page's authored **SEO Title** when a merchandiser
 * filed one for this folder, else the folder's own name (UCR-3060).
 *
 * Deliberately not the page's `name` in between — that's an admin label ("Learn Listing — Hydroponics")
 * rather than a title — nor the page type's `title` setting, which is the heading rendered above the
 * grid; a merchandiser who wants that string in the tab as well puts it in SEO Title.
 */
function learnListingTitle(listing: LearnListing, page: PageWithComponentData | null): string {
    return resolvePageTitle(page?.pageTitle) ?? (listing.folder ? `${listing.folder.name} | Learn` : 'Learn');
}

/** Rebuild the `/<siteId>/<locale>/learn/` prefix (everything up to and including `/learn/`). */
function learnBase(pathname: string): string {
    const marker = '/learn/';
    return pathname.slice(0, pathname.indexOf(marker) + marker.length);
}

/**
 * `/learn/*` route (UCR-2725) — dispatches between an **Article page** and a **Learn listing**.
 *
 * Articles are resolved live: the **last path segment is the slug = the Page Designer page ID**, so a
 * newly published article is reachable immediately — no build, map, or sync. If the last segment
 * isn't an Article page, the full path is treated as a library folder and an auto-generated listing
 * is rendered instead.
 *
 * Folder paths (the category, e.g. `hydroponics`) come from the article's *default (classification)
 * folder* via the `pd-taxonomy` Custom API ({@link getArticleFolder} / {@link getLearnCatalog}):
 * Page Designer pages are walled off from the OCAPI SHOP/DATA APIs, so an on-platform Custom API
 * (Script API `Content.getClassificationFolder()`) is the only runtime way to read them. To give each
 * article one clean URL, any non-canonical path (bare `/learn/<slug>` or a wrong folder prefix) is
 * 301-redirected to `/learn/<default-folder>/<slug>`. See `ai-docs/learn-article-urls/`.
 */
export async function loader(args: Route.LoaderArgs): Promise<LearnRouteData> {
    const logger = getLogger(args.context);

    const segments = (args.params['*'] ?? '').split('/').filter(Boolean);
    const slug = segments[segments.length - 1];
    if (!slug) {
        throw new Response('Not Found', { status: 404 });
    }
    logger.debug('Learn: loader starting', { path: segments.join('/') });

    const requestUrl = new URL(args.request.url);
    const base = learnBase(requestUrl.pathname);
    // Resolved public origin, not the lambda-internal request origin, so og:url matches canonical.
    const publicOrigin = getPublicOrigin(args.request);

    // Article first: the last segment is the page ID. A hit renders the article.
    let page = await fetchPageWithComponentData(args, { pageId: slug });

    // `/learn/**` is lowercase-canonical (UCR-3053), so the slug arriving here is lowercase while the
    // authored page id may be mixed-case — and SCAPI resolves a page strictly by id. Map the URL form
    // back to the authored form and fetch that. `articleId` is used for platform lookups only; the URL
    // and canonical path keep the lowercase slug, because redirecting to the authored casing would
    // fight the canonical-case middleware and bounce the URL between the two forever.
    let articleId = slug;
    if (!page) {
        const authoredId = resolveArticleIdIgnoringCase(await getLearnCatalog(args.context), slug);
        if (authoredId && authoredId !== slug) {
            page = await fetchPageWithComponentData(args, { pageId: authoredId });
            if (page) {
                articleId = authoredId;
            }
        }
    }

    // Article Listing page (UCR-2756): in Page Designer preview the editor resolves the page here by
    // its own id (design mode overrides the slug). Render it as a LISTING — its regions around the
    // folder grid — not as an article (this page type has no `main` region). The grid is built for the
    // URL's folder when it's a real folder, else an empty listing.
    if (page && page.typeId === ARTICLE_LISTING_PAGE_TYPE) {
        const catalog = await getLearnCatalog(args.context);
        const listing = getLearnListing(catalog, segments.join('/')) ?? {
            folder: null,
            subfolders: [],
            articles: [],
            breadcrumb: [],
        };
        const canonicalPathname = base + (listing.folder?.path ?? '');
        return {
            kind: 'listing',
            listing,
            page,
            footerTheme: resolveLastComponentTheme(page, LISTING_REGION_IDS),
            pageUrl: buildCanonicalUrl(publicOrigin, canonicalPathname),
            title: learnListingTitle(listing, page),
            metaDescription: resolvePageMetaDescription(page),
            ogImageUrl: resolveOgImage({ origin: publicOrigin, page, regionIds: LISTING_REGION_IDS }),
            analytics: learnListingAnalytics(listing),
        };
    }

    if (!page) {
        // Not an article — treat the full path as a library folder and render its listing.
        const catalog = await getLearnCatalog(args.context);
        const listing = getLearnListing(catalog, segments.join('/'));
        if (!listing) {
            throw new Response('Not Found', { status: 404 });
        }
        // Authored Article Listing page for this exact folder, if a merchandiser filed one there
        // (UCR-2756). Fetched by id — reliable, and only when one exists.
        const listingPage = listing.listingPageId
            ? await fetchPageWithComponentData(args, { pageId: listing.listingPageId })
            : null;
        const canonicalPathname = base + (listing.folder?.path ?? '');
        return {
            kind: 'listing',
            listing,
            page: listingPage,
            footerTheme: resolveLastComponentTheme(listingPage, LISTING_REGION_IDS),
            pageUrl: buildCanonicalUrl(publicOrigin, canonicalPathname),
            title: learnListingTitle(listing, listingPage),
            // Folders without an authored listing page have no `listingPage`, so both resolve to
            // "nothing" and the Black Magic mark respectively — the auto-generated grid carries no
            // authored SEO content of its own.
            metaDescription: resolvePageMetaDescription(listingPage),
            ogImageUrl: resolveOgImage({ origin: publicOrigin, page: listingPage, regionIds: LISTING_REGION_IDS }),
            analytics: learnListingAnalytics(listing),
        };
    }

    // Article branch: resolve the default folder for the canonical URL + breadcrumb. Keyed by the
    // authored page id (which is `slug` unless the URL differed only by case), since the folder lookup
    // goes to the platform.
    const folder = await getArticleFolder(args.context, articleId);

    // Canonical path under `/learn` — the article's default folder + slug. The canonical <link> tag is
    // generated centrally in root.tsx from the *request* URL, so to make it correct (and to give the
    // article one clean URL) we 301 any non-canonical path — a bare `/learn/<slug>` or a wrong/extra
    // folder prefix — to the default-folder URL. Skipped in PD edit/preview, where the merchandiser's
    // URL and its mode/pdToken params must be preserved. If the folder lookup failed (`!exists`), we
    // don't redirect — the article still renders at the requested URL (fail-safe).
    const canonicalPath = folder.path ? `${folder.path}/${slug}` : slug;
    const previewing = isDesignModeActive(args.request) || isPreviewModeActive(args.request);
    if (folder.exists && !previewing && segments.join('/') !== canonicalPath) {
        throw redirect(base + canonicalPath, 301);
    }
    const canonicalPathname = base + canonicalPath;

    // Breadcrumb folder crumbs (each links to its Learn listing); the article title is appended in view.
    const breadcrumb: LearnCrumb[] = [];
    let acc = '';
    for (const segment of folder.segments) {
        acc = acc ? `${acc}/${segment.id}` : segment.id;
        breadcrumb.push({ name: segment.name, href: `/learn/${acc}` });
    }

    // Authored SEO Title, then the page's name, then a generic label. Routed through the shared helper
    // so a blank-but-present SEO Title falls through rather than emptying the tag (UCR-3060).
    const title = resolvePageTitle(page.pageTitle, page.name) ?? 'Article';

    // Article JSON-LD (UCR-2783). The thumbnail + description are page-level settings on the Article
    // page type (see ArticlePageMetadata), delivered in `page.data`. The @id/author/image use the
    // public origin (not the internal request URL). Non-article listings don't emit this.
    const settings = page.data as { thumbnail?: WidenImageValue; articleDescription?: string } | undefined;
    const thumb = settings?.thumbnail;
    const thumbUrl = thumb?.webpURL || thumb?.fileURL;
    const articleSchema = generateArticleSchema({
        origin: publicOrigin,
        path: canonicalPathname,
        headline: title,
        // Raw authored text, deliberately not the truncated meta description — structured data has
        // no snippet-length limit. SEO Description outranks the internal page blurb, which is often
        // an admin note rather than prose. Kept identical to the Landing page, which feeds this same
        // builder. (UCR-3023)
        description: settings?.articleDescription ?? page.pageDescription ?? page.description,
        imageUrls: thumbUrl ? [widenImageUrl(thumbUrl)] : [],
        config: getConfig(args.context),
    });

    return {
        kind: 'article',
        page,
        footerTheme: resolveLastComponentTheme(page, ['main']),
        pageUrl: buildCanonicalUrl(publicOrigin, canonicalPathname),
        title,
        // The article's SEO Description, then the internal page description. Note this is *not*
        // `articleDescription` — that attribute is listing-card copy, and is what the JSON-LD above
        // uses. Merchandisers who want a distinct search snippet fill in SEO Description. (UCR-3023)
        metaDescription: resolvePageMetaDescription(page),
        // The Article page type declares a Thumbnail, so it wins — the same image the JSON-LD uses,
        // keeping the social card and the rich result in agreement — then the first image in the body.
        ogImageUrl: resolveOgImage({
            origin: publicOrigin,
            thumbnail: thumb,
            page,
            regionIds: ['main'],
        }),
        breadcrumb,
        articleSchema,
        analytics: {
            page: { pageName: buildPageName('learn', slug), pageType: 'article' },
            article: { id: page.id ?? '', name: title, dates: '' },
            pageEvent: 'articleView',
        },
    };
}

/** Article breadcrumb: `Learn › <folder crumbs> › <article title>`. */
function ArticleBreadcrumb({ crumbs, title }: { crumbs: LearnCrumb[]; title: string }): ReactElement {
    return (
        <nav aria-label="Breadcrumb" className="mb-6">
            <ol className="flex flex-wrap items-center text-sm leading-5 text-foreground">
                <li className="flex items-center">
                    <Link to="/learn" className="hover:underline">
                        Learn
                    </Link>
                </li>
                {crumbs.map((crumb) => (
                    <li key={crumb.href} className="flex items-center">
                        <ChevronRight className="mx-1 size-3" aria-hidden />
                        <Link to={crumb.href} className="hover:underline">
                            {crumb.name}
                        </Link>
                    </li>
                ))}
                <li className="flex items-center">
                    <ChevronRight className="mx-1 size-3" aria-hidden />
                    <span className="font-medium">{title}</span>
                </li>
            </ol>
        </nav>
    );
}

/**
 * Renders either an authored Article page (content from its `main` region) or an auto-generated
 * Learn listing, per the loader's discriminant.
 */
export default function LearnRoute({ loaderData }: { loaderData: LearnRouteData }): ReactElement {
    const nonce = useRouteLoaderData<typeof rootLoader>('root')?.nonce ?? undefined;

    if (loaderData.kind === 'listing') {
        const { listing, page, pageUrl, title, metaDescription, ogImageUrl } = loaderData;
        return (
            <>
                <SeoMeta
                    title={title}
                    description={metaDescription}
                    openGraph={{ type: 'website', url: pageUrl, image: ogImageUrl }}
                />
                <LearnListingView listing={listing} page={page} />
            </>
        );
    }

    const { page, pageUrl, title, breadcrumb, metaDescription, ogImageUrl } = loaderData;
    // The article header (breadcrumb) is capped to the content width via `.section-container`, but the
    // `main` region is rendered full-width — matching the homepage/PLP — so full-bleed Page Designer
    // components (Content Container, Full Width Image, …) bleed edge-to-edge and constrain their own
    // interior with an inner `.section-container`. Wrapping the region in a `max-w` container (as before)
    // capped those components' themed backgrounds at 1440px instead of the window. (UCR-2774)
    return (
        // `-mt-8` cancels the global `main` top padding so the top-most element sits flush to the header
        // (matching the homepage/PLP full-width pattern) — when the breadcrumb section is hidden the
        // `main` region's first Page Designer component has no gap above it. (UCR-2760-style flush)
        <div className="-mt-8">
            <SeoMeta
                title={title}
                description={metaDescription}
                openGraph={{ type: 'article', url: pageUrl, image: ogImageUrl }}
            />
            {loaderData.articleSchema && <JsonLd data={loaderData.articleSchema} id="article-schema" nonce={nonce} />}
            <BreadcrumbSection className="section-container pt-6">
                <ArticleBreadcrumb crumbs={breadcrumb} title={title} />
            </BreadcrumbSection>
            <Region page={page} regionId="main" />
        </div>
    );
}
